import { QuestionnaireFormProps } from '@getpopsure/qnr-framework';
import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { flushGenericQuestionnaire } from 'actions/genericQuestionnaire';
import routes from 'constants/routes';
import { createExpatSpainQuote } from 'features/expatSpain/actions';
import { ExpatSpain, ExpatSpainDependent } from 'features/expatSpain/models';
import { useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router';
import { useSafeTranslation } from 'shared/i18n';
import { RequestProcessing } from 'SignupQuestionnaire/components/Processing/RequestProcessing';

export const QuoteProcessing = ({
  questionnaireAnswers,
  onSubmitValue,
}: QuestionnaireFormProps<ExpatSpain | ExpatSpainDependent>) => {
  const { t } = useSafeTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const { mutateAsync: submitQuotes } = useMutation({
    mutationFn: () => {
      const { dateOfBirth, occupation, otherOccupation } = questionnaireAnswers;

      if (!dateOfBirth || !occupation) {
        throw new Error('Missing fields required to fetch Expat ES quote');
      }

      return createExpatSpainQuote({
        dateOfBirth,
        occupation,
        otherOccupation,
      });
    },
    onSuccess: ({ data }) => {
      onSubmitValue(data);
    },
    onError: (error) => {
      Sentry.captureException(error, {
        tags: {
          feature: 'QUOTE',
          vertical: 'EXPAT_ES',
        },
        extra: {
          description: 'Error happened while fetching Expat ES quotes',
        },
      });
    },
  });

  const textList: string[] = [
    t('expatSpain.qnr.quoteProcessing.title', 'Processing quote...'),
  ];

  const description: string = t(
    'expatSpain.qnr.quoteProcessing.subtitle',
    "We're preparing a quote based on the answers you provided."
  );

  const handleErrorRecovery = () => {
    if ('mainPolicyId' in questionnaireAnswers) {
      const { mainPolicyId } = questionnaireAnswers;
      dispatch(flushGenericQuestionnaire('expatSpain'));
      const redirectPath = generatePath(
        routes.policies.expatSpain.dependents.path,
        {
          mainPolicyId,
        }
      );
      history.push(redirectPath);
    } else {
      dispatch(flushGenericQuestionnaire('expatSpain'));
      history.push(routes.policies.expatSpain.path);
    }
  };

  return (
    <RequestProcessing
      textList={textList}
      description={description}
      requestFn={submitQuotes}
      handleErrorRecovery={handleErrorRecovery}
      shouldSubmitScreen={false}
    />
  );
};
